import { useEffect, useRef } from 'react';
import type RenderEvent from 'ol/render/Event';
import TileLayer from 'ol/layer/Tile';
import { useMapContext } from '../../map/MapContext';
import './compare-swipe.css';

/**
 * Swipe/compare mode (v1.1.0's "Map Comparison" scope, pulled forward into
 * v0.5.0 per the plan). While active, the second compare-date raster layer
 * is clipped to the right of a draggable vertical handle using OL's
 * standard prerender/postrender canvas-clip technique, so both dated
 * rasters render from the same map view simultaneously, split at the
 * handle position.
 */
export function CompareSwipe() {
  const { map, layers, compareMode, compareDates, dates, setCompareDates, swipePosition, setSwipePosition } =
    useMapContext();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const rightLayer = layers.find((l) => l.kind === 'raster' && l.date === compareDates[1])?.layer as
    | TileLayer
    | undefined;

  useEffect(() => {
    if (!compareMode || !rightLayer) return;

    function prerender(evt: RenderEvent) {
      const ctx = evt.context as CanvasRenderingContext2D;
      const size = map.getSize();
      if (!size) return;
      const width = size[0] * swipePosition;
      const mapRect = ctx.canvas;
      ctx.save();
      ctx.beginPath();
      ctx.rect(width, 0, mapRect.width - width, mapRect.height);
      ctx.clip();
    }
    function postrender(evt: RenderEvent) {
      (evt.context as CanvasRenderingContext2D).restore();
    }

    rightLayer.on('prerender', prerender);
    rightLayer.on('postrender', postrender);
    map.render();
    return () => {
      rightLayer.un('prerender', prerender);
      rightLayer.un('postrender', postrender);
      map.render();
    };
  }, [compareMode, rightLayer, swipePosition, map]);

  useEffect(() => {
    function onMove(e: PointerEvent) {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      setSwipePosition(frac);
    }
    function onUp() {
      draggingRef.current = false;
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [setSwipePosition]);

  if (!compareMode) return null;

  return (
    <div ref={containerRef} className="compare-swipe-overlay">
      <div className="compare-swipe-labels">
        <select
          value={compareDates[0]}
          onChange={(e) => setCompareDates([e.target.value, compareDates[1]])}
        >
          {dates.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <span>vs</span>
        <select
          value={compareDates[1]}
          onChange={(e) => setCompareDates([compareDates[0], e.target.value])}
        >
          {dates.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
      <div
        className="compare-swipe-handle"
        style={{ left: `${swipePosition * 100}%` }}
        onPointerDown={() => {
          draggingRef.current = true;
        }}
      />
    </div>
  );
}
